// Fachada do domínio CS (pós-venda Enterprise) — adapters Supabase → service.
// Contrato da venda, planilha de vidas e, mais adiante, os gatilhos.

import { csService } from './core';
import { lerPlanilhaVidas } from '@infra/parsers/planilhaVidas';
import type {
  ArquivoPlanilha, NegociacaoPlanilha, PlanilhaLinha, PlanilhaValidada, PlanoDoCatalogo,
} from '@modules/cs';

// ─── Contrato ────────────────────────────────────────────────────
// `registrarContratoDaVenda` é chamado DEPOIS da venda já ter sido
// registrada. Non-fatal por contrato: erro aqui vira contrato pendente,
// nunca venda perdida.
export const registrarContratoDaVenda = csService.registrarContratoDaVenda;
export const getContratoBySale       = csService.getContratoBySale;
export const getContratoByEmpresa    = csService.getContratoByEmpresa;
export const listContratoDocumentos  = csService.listDocumentos;
export const marcarContratoDocumento = csService.marcarDocumento;
export const listCompetencias         = csService.listCompetencias;
export const criarCompetenciaAtual    = csService.criarCompetenciaAtual;
export const previaBoleto             = csService.previaBoleto;
export const dadosDoBoleto            = csService.dadosDoBoleto;
export const marcarCompetenciaPaga    = csService.marcarCompetenciaPaga;
export const contratoCamposFaltando  = csService.camposFaltando;

// ─── Planilha de vidas ───────────────────────────────────────────
export const validarPlanilhaDaEmpresa = csService.validarPlanilhaDaEmpresa;
export const prepararValidacaoDaEmpresa = csService.prepararValidacaoDaEmpresa;
export const importarVidas            = csService.importarVidas;
export const listVidas                = csService.listVidas;
export const criarVida                = csService.criarVida;
export const atualizarVida            = csService.atualizarVida;
export const removerVida              = csService.removerVida;
export const listImportacoes          = csService.listImportacoes;
export const listHistoricoImportacoes = csService.listHistoricoImportacoes;

// ─── Rascunhos de vidas ──────────────────────────────────────────
// Linhas da planilha que não viraram vida. Fora de cs.vidas: não contam vida,
// não vão ao Hub, não entram no boleto.
export const listRascunhos            = csService.listRascunhos;
export const salvarRascunho           = csService.salvarRascunho;
export const resolverRascunho         = csService.resolverRascunho;
export const descartarRascunho        = csService.descartarRascunho;

// ─── Área de Implantação ─────────────────────────────────────────
// Leitura de uma equipe interna sobre as empresas liberadas. A permissão é
// do banco; o `currentUserIsImplantacao` daqui só evita oferecer uma tela
// que vai negar.
export const currentUserIsImplantacao = csService.currentUserIsImplantacao;
export const listClientesImplantacao  = csService.listClientesImplantacao;
export const getClienteImplantacao    = csService.getClienteImplantacao;

// ─── Clientes do Hub (/clientes, aba Hub) ────────────────────────
// Espelho de TODAS as empresas do BlueHub — owner, CS e Implantação veem;
// só owner e CS vinculam/desvinculam. A permissão é do banco.
export const podeVerClientesHub   = csService.podeVerClientesHub;
export const podeVincularEmpresaHub = csService.podeVincularEmpresaHub;
export const listClientesHub      = csService.listClientesHub;
export const ultimaSincronizacaoHub = csService.ultimaSincronizacaoHub;
export const vincularEmpresaHub   = csService.vincularEmpresaHub;
export const desvincularEmpresaHub = csService.desvincularEmpresaHub;
export const buscarEmpresasParaVinculo = csService.buscarEmpresasParaVinculo;
export const listContasHubDaEmpresa = csService.listContasHubDaEmpresa;
export const empresaTravadaPeloHub = csService.empresaTravadaPeloHub;

// ─── Ficha do cliente (/clientes/:empresaId) ─────────────────────
// Uma rota, dois públicos — o nível (completo × reduzido) vem do banco.
export const obterFichaCliente = csService.obterFichaCliente;
export const listGatilhosEmpresa = csService.listGatilhosEmpresa;

// ─── Clientes Ativos (Enterprise) ────────────────────────────────
// Empresas com contrato enviado, com o status do contrato e do boleto.
export const listCarteiraDeContratos = csService.listCarteiraDeContratos;
export const ocultarEmpresaDaCarteira = csService.ocultarEmpresaDaCarteira;

// ─── Rota de integração (n8n × Hub) ──────────────────────────────
// Escolhida no Super Admin. A leitura por empresa serve às telas; o disparo
// é recusado pelo servidor quando a rota é Hub e o transporte ainda não existe.
export const getRotaIntegracao      = csService.getRotaIntegracao;
export const getRotaGlobal          = csService.getRotaGlobal;
export const salvarRotaGlobal       = csService.salvarRotaGlobal;
export const buscarEmpresasParaRota = csService.buscarEmpresasParaRota;

// ─── Envios desativados por empresa ──────────────────────────────
// Também do Super Admin. A tela trava o botão; o servidor recusa o disparo.
export const listEnviosBloqueados      = csService.listEnviosBloqueados;
export const listEnviosBloqueadosAdmin = csService.listEnviosBloqueadosAdmin;
export const bloquearEnvio             = csService.bloquearEnvio;
export const desbloquearEnvio          = csService.desbloquearEnvio;

// ─── Gatilhos ────────────────────────────────────────────────────
export const montarPayloadGatilho  = csService.montarPayloadGatilho;
export const registrarGatilho      = csService.registrarGatilho;
export const gatilhoJaTeveSucesso  = csService.gatilhoJaTeveSucesso;
export const conferirContagemDeVidas  = csService.conferirContagem;

export interface PlanilhaLidaEValidada {
  validacao: PlanilhaValidada;
  arquivo: ArquivoPlanilha;
  /** Cabeçalhos que o parser não reconheceu — a UI avisa em vez de sumir com eles. */
  colunasIgnoradas: string[];
  negociacao: NegociacaoPlanilha | null;
  /** As linhas como vieram do arquivo — a tela corrige em cima delas. */
  linhas: PlanilhaLinha[];
  /** Revalida a planilha inteira depois de uma correção, sem reler o arquivo
   *  nem voltar ao banco. */
  revalidar: (linhas: PlanilhaLinha[]) => PlanilhaValidada;
}

/**
 * Lê o arquivo e valida numa chamada só. É o ponto de composição entre a
 * infra (parser de CSV/xlsx) e o núcleo (regras de CPF, data e catálogo) —
 * a UI não conhece nenhum dos dois.
 *
 * Não grava nada: é o preview, para o comercial corrigir as linhas com erro
 * ali mesmo antes de confirmar.
 */
export const lerEValidarPlanilha = async (params: {
  file: File;
  empresaId: string;
  planos: PlanoDoCatalogo[];
  hoje?: Date;
}): Promise<PlanilhaLidaEValidada> => {
  const { linhas, formato, hash, colunasIgnoradas, negociacao } = await lerPlanilhaVidas(params.file);

  const revalidar = await csService.prepararValidacaoDaEmpresa({
    empresaId: params.empresaId,
    planos: params.planos,
    hoje: params.hoje ?? new Date(),
  });

  return {
    validacao: revalidar(linhas),
    arquivo: { nome: params.file.name, formato, hash, blob: params.file },
    colunasIgnoradas,
    negociacao,
    linhas,
    revalidar,
  };
};

export {
  CONTRATO_CAMPO_LABELS,
  QUALIFICACAO_LABELS,
  QUALIFICACAO_PADRAO,
  qualificacaoOuPadrao,
  CONTRATO_STATUS_LABELS,
  DOCUMENTO_LABELS,
  GATILHO_LABELS,
  GATILHO_STATUS_LABELS,
  estadoDaFicha,
  MODALIDADE_AJUDA,
  MODALIDADE_LABELS,
  MODALIDADE_PADRAO,
  MODELO_HUB_LABELS,
  rotuloModeloHub,
  rotuloStatusHub,
  STATUS_HUB_LABELS,
  STATUS_PADRAO,
  filtrarClientesHub,
  SITUACAO_IMPLANTACAO_LABELS,
  STATUS_BOLETO_LABELS,
  STATUS_ENVIO_CONTRATO_LABELS,
  VIDA_MAIS_COBRANCA_PADRAO,
  competenciaAtualDoBoleto,
  motivoBloqueioLiberacao,
  motivoContratoJaEnviado,
  podeReenviarContrato,
  pagamentoQuitado,
  statusBoleto,
  statusEnvioContrato,
  CAMPOS_DE_ASSINATURA,
  PSICOLOGO_ABA,
  PSICOLOGO_COLUNAS,
  linhasPsicologoNaTela,
  comTotalPassLegado,
  dedupeKeyDe,
  documentosNecessarios,
  contaComoVida,
  ehDependente,
  ehPlanoVidaMais,
  ehSemPlano,
  PLANO_SEM_PLANO_TEXTO,
  VIDA_TIPO_LABELS,
  empresaGeraContrato,
  empresaRecebeBoleto,
  emptyContratoDados,
  enderecoDoErro,
  chaveDaLinha,
  chaveDoErro,
  confirmacaoDoBoleto,
  PISO_VIDAS_BOLETO_HUB,
  MESES_BONIFICACAO,
  MESES_BONIFICACAO_MAX,
  BOLETO_SO_PELO_N8N,
  compararComHub,
  conferenciaParaEnvio,
  isentoAteDe,
  mesesAteIsento,
  janelaVencimento,
  problemaDoVencimento,
  problemasDaConferencia,
  subtotalAdicional,
  totalConferenciaCents,
  totalVidasConferidas,
  corrigirLinhaDaPlanilha,
  descartarLinhaDaPlanilha,
  IDADE_MAXIMA,
  IDADE_MINIMA,
  formatCnpj,
  formatCpf,
  formatarDataBr,
  idadeEm,
  isValidCnpj,
  isValidCpf,
  isValidEmail,
  marcadoNaPlanilha,
  mascararCpf,
  mascararDataBr,
  mascararTelefone,
  maskCpf,
  normalizeText,
  motivoEnvioBloqueado,
  motivoRotaHub,
  motivoSemBoleto,
  motivoSemContrato,
  onlyDigits,
  TIPOS_BLOQUEAVEIS,
  parseDataNascimento,
  planoLiberaAcesso,
  podeLiberarImplantacao,
  situacaoImplantacao,
  vidasContratadas,
  montarLoteDaImportacao,
  linhasComErroNovo,
  dependentesDaLinha,
  validarLinhaDoRascunho,
  resumoDaImportacao,
} from '@modules/cs';

export type {
  ArquivoPlanilha,
  LinhaCarteira,
  TotaisCarteira,
  ClienteHub,
  ClienteImplantacao,
  ClienteImplantacaoDetalhe,
  ClienteImplantacaoResultado,
  Competencia,
  Contrato,
  ContaHubDaEmpresa,
  ContratoCampoTexto,
  ContratoDados,
  RepresentanteQualificacao,
  ContratoModalidade,
  PlanoDoCatalogo,
  ContratoDocumento,
  ContratoStatus,
  DocumentoTipo,
  EmpresaParaRota,
  EmpresaParaVinculo,
  EnvioBloqueado,
  EnvioBloqueadoRegistro,
  EstadoFicha,
  FiltrarClientesHubOptions,
  GatilhoEmpresa,
  GatilhoStatus,
  GatilhoTipo,
  GatilhoTransporte,
  GrupoHub,
  Importacao,
  ModeloHub,
  RepresentanteHub,
  RotaIntegracao,
  RotaVia,
  NegociacaoPlanilha,
  PlanilhaErro,
  PlanilhaLinha,
  PlanilhaValidada,
  PreviaBoleto,
  AdicionalConferido,
  ItemDoCatalogo,
  ConferenciaBoleto,
  ConferenciaEnviada,
  HubDoPreparo,
  PreparoBoleto,
  SituacaoImplantacao,
  StatusBoleto,
  StatusEnvioContrato,
  StatusHubConhecido,
  SugestaoVinculoHub,
  TotalPassLegado,
  UltimaSincronizacaoHub,
  Vida,
  VidaImportada,
  VidaMaisCobranca,
  VidaTipo,
  VinculoFiltro,
  VidaRascunho,
  VidaRascunhoStatus,
  ImportacaoHistorico,
  LinhaParaRascunho,
} from '@modules/cs';
